

import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import airports from "../../../Data/AirportList.json";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  RiCalendarLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiSwapLine,
  RiArrowRightLine,
  RiExchangeLine,
  RiStackLine,
} from "react-icons/ri";
import "./SearchFlightPanel1.css";

const SearchFlightPanel1 = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  
  // Original state management exactly as in SearchFlightPanel
  const [departureDate, setDepartureDate] = useState(new Date());
  const [returnDate, setReturnDate] = useState(new Date(new Date().setDate(new Date().getDate() + 7)));
  const [departureResults, setDepartureResults] = useState([]);
  const [arrivalResults, setArrivalResults] = useState([]);
  const [locationFrom, setLocationFrom] = useState("HYD");
  const [locationTo, setLocationTo] = useState("BOM");
  const [displayFrom, setDisplayFrom] = useState("Hyderabad(HYD), India");
  const [displayTo, setDisplayTo] = useState("Mumbai(BOM), India");
  const [selectedDeparture, setSelectedDeparture] = useState(null);
  const [selectedArrival, setSelectedArrival] = useState(null);
  const [cityFrom, setCityFrom] = useState("Hyderabad");
  const [cityTo, setCityTo] = useState("Mumbai");
  const [from, setFrom] = useState("Hyderabad, India");
  const [to, setTo] = useState("Mumbai, India");
  const [trips, setTrips] = useState([
    {
      from: "HYD, India",
      to: "BOM, India",
      locationFrom: "HYD",
      locationTo: "BOM",
      displayFrom: "Hyderabad(HYD)",
      displayTo: "Mumbai(BOM)",
      cityFrom: "Hyderabad",
      cityTo: "Mumbai",
      departureDate: new Date(),
      departureResults: [],
      arrivalResults: [],
    },
  ]);
  const [adults, setAdults] = useState(1);
  const [kids, setKids] = useState(0);
  const [infants, setInfants] = useState(0);
  const [cabinClass, setCabinClass] = useState("ECONOMY");
  const [preferred, setPreferred] = useState("REGULAR");
  const [isDirect, setIsDirect] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDomestic, setIsDomestic] = useState("DOMESTIC");
  const [journeyType, setJourneyType] = useState("ONEWAY");
  
  // Refs for new UI
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);

  // Multi-city states (for UI only)
  const [multiCitySegments, setMultiCitySegments] = useState([
    {
      id: 0,
      from: "Hyderabad(HYD), India",
      to: "Mumbai(BOM), India",
      date: new Date()
    },
    {
      id: 1,
      from: "Mumbai(BOM), India",
      to: "Delhi(DEL), India",
      date: new Date(new Date().setDate(new Date().getDate() + 5))
    }
  ]);

  // Original CustomDateInput component
  const CustomDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
    <div className="sf1-input-group">
      <input
        type="text"
        className="sf1-form-control"
        value={value}
        onClick={onClick}
        ref={ref}
        readOnly
        placeholder={placeholder || "Select date"}
      />
      <button className="sf1-btn-secondary" type="button" onClick={onClick}>
        <RiCalendarLine />
      </button>
    </div>
  ));

  // Original validate function
  const validate = () => {
    let temp = {};
    temp.from = from === "" ? false : true;
    temp.to = to === "" ? false : true;
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  // Original airport search functions
  const handleDepartureAirportSearch = (e) => {
    const value = e.target.value;
    setDisplayFrom(value);
    if (value.length > 0) {
      const lowerValue = value.toLowerCase();
      const filteredAirports = airports
        .filter(
          (airport) =>
            airport.display === true &&
            airport.airportCode.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 10);
      setDepartureResults(filteredAirports);
      if (filteredAirports.length === 0) {
        const filteredAirports1 = airports
          .filter(
            (airport) =>
              airport.airportCode.toLowerCase().includes(lowerValue) ||
              airport.cityName.toLowerCase().includes(lowerValue) ||
              (airport.airportName.toLowerCase().includes(lowerValue) &&
                (!selectedArrival ||
                  airport.airportCode !== selectedArrival.airportCode))
          )
          .slice(0, 10);
        setDepartureResults(filteredAirports1);
      }
    } else {
      setDepartureResults([]);
    }
  };

  const handleDepartureAirportClick = (e) => {
    setArrivalResults([]);
    let filteredAirports = airports
      .filter(function (p) {
        return p.display === true;
      })
      .slice(0, 10);
    setDepartureResults(filteredAirports);
    fromInputRef.current.select();
  };

  const handleDepartureSelect = (data) => {
    if (selectedArrival && selectedArrival.airportCode === data.airportCode) {
      alert("Departure and arrival airports cannot be the same.");
      setSelectedArrival(null);
      setDisplayTo("");
      return;
    } else {
      var DF =
        data.cityName + "(" + data.airportCode + "), " + data.countryName;
      if (data.countryCode !== "IN") {
        setIsDomestic("INTERNATIONAL");
      }
      setDisplayFrom(DF);
      setLocationFrom(data.airportCode);
      setCityFrom(data.cityName);
      setFrom(data.cityName);
      setSelectedDeparture(data);
      setDepartureResults([]);
    }
  };

  const handleArrivalAirportSearch = (e) => {
    const value = e.target.value;
    setDisplayTo(value);
    if (value.length > 0) {
      const lowerValue = value.toLowerCase();
      const filteredAirports = airports
        .filter(
          (airport) =>
            airport.airportCode.toLowerCase().includes(lowerValue) ||
            airport.cityName.toLowerCase().includes(lowerValue) ||
            airport.airportName.toLowerCase().includes(lowerValue)
        )
        .slice(0, 10);
      setArrivalResults(filteredAirports);
      if (filteredAirports.length === 0) {
        const filteredAirports1 = airports
          .filter((airport) =>
            airport.cityName.toLowerCase().includes(value.toLowerCase())
          )
          .slice(0, 10);
        setArrivalResults(filteredAirports1);
      }
    } else {
      setArrivalResults([]);
    }
  };

  const handleArrivalSelect = (data) => {
    if (
      selectedDeparture &&
      selectedDeparture.airportCode === data.airportCode
    ) {
      alert("Arrival and departure airports cannot be the same.");
      setSelectedDeparture(null);
      setDisplayFrom("");
      return;
    } else {
      var DF =
        data.cityName + "(" + data.airportCode + "), " + data.countryName;
      if (data.countryCode !== "IN") {
        setIsDomestic("INTERNATIONAL");
      }
      setDisplayTo(DF);
      setLocationTo(data.airportCode);
      setCityTo(data.cityName);
      setTo(data.cityName);
      setSelectedArrival(data);
      setArrivalResults([]);
    }
  };

  const handleArrivalAirportClick = (e) => {
    setDepartureResults([]);
    let filteredAirports = airports
      .filter(function (p) {
        return p.display === true;
      })
      .slice(0, 10);
    setArrivalResults(filteredAirports);
    toInputRef.current.select();
  };

  // Original passenger and preference handlers
  const handleClassType = (e) => {
    setCabinClass(e.target.value);
  };

  const handleDirectType = (e) => {
    setIsDirect(e.target.checked);
  };

  const handleConnectingType = (e) => {
    setIsConnecting(e.target.checked);
  };

  const handlePreferredType = (e) => {
    setPreferred(e.target.value);
  };

  const handleIncreaseAdults = (e) => {
    e.stopPropagation();
    setAdults(parseFloat(adults + 1));
  };

  const handleDecreaseAdults = (e) => {
    e.stopPropagation();
    if (adults !== 1) {
      setAdults(parseFloat(adults - 1));
    }
  };

  const handleIncreaseKids = (e) => {
    e.stopPropagation();
    setKids(parseFloat(kids + 1));
  };

  const handleDecreaseKids = (e) => {
    e.stopPropagation();
    if (kids !== 0) {
      setKids(parseFloat(kids - 1));
    }
  };

  const handleIncreaseInfants = (e) => {
    e.stopPropagation();
    setInfants(parseFloat(infants + 1));
  };

  const handleDecreaseInfants = (e) => {
    e.stopPropagation();
    if (infants !== 0) {
      setInfants(parseFloat(infants - 1));
    }
  };

  const handleDepartureDateChange = (date) => {
    const selectedDate = date;
    setDepartureDate(selectedDate);
    if (returnDate && selectedDate && returnDate < selectedDate) {
      setReturnDate(selectedDate);
    }
  };

  const handleReturnDateChange = (date) => {
    setReturnDate(date);
  };

  const handleJourneyTypeChange = (e, type) => {
    e.preventDefault();
    setJourneyType(type);
  };

  // Fixed GetFlightDestinations function
  function GetFlightDestinations() {
    const fvalues = [];
    
    if (journeyType === "MULTI") {
      // For multi-city, use the segments
      for (var i = 0; i < multiCitySegments.length; i++) {
        const segment = multiCitySegments[i];
        // Parse the from and to values to extract airport codes
        const fromMatch = segment.from.match(/\(([^)]+)\)/);
        const toMatch = segment.to.match(/\(([^)]+)\)/);
        
        fvalues.push({
          from: fromMatch ? fromMatch[1] : "HYD",
          to: toMatch ? toMatch[1] : "BOM",
          departureDate: moment(segment.date).format("YYYY-MM-DD"),
          returnDate: null,
        });
      }
    } else {
      // For one-way or round trip
      fvalues.push({
        from: locationFrom,
        to: locationTo,
        departureDate: moment(departureDate).format("YYYY-MM-DD"),
        returnDate: journeyType === "RETURN" ? moment(returnDate).format("YYYY-MM-DD") : null,
      });
    }
    
    return fvalues;
  }

  // Original handleSearch
  const handleSearch = (e) => {
    e.preventDefault();
    console.log(user);
    if (validate()) {
      const searchQuery = {
        journeyType: journeyType,
        isDomestic: isDomestic,
        adults: adults,
        kids: kids,
        infants: infants,
        cabinClass: cabinClass,
        companyId: user?.isSupplierAPI === true ? user.cid : user.parentCompanyId,
        preferred: preferred,
        isDirectFlight: isDirect,
        isConnectingFlight: isConnecting,
        flightTravelerPoints: GetFlightDestinations(),
      };
      console.log("Search Query:", searchQuery);
      navigate(
        "/template/flights/" + adults + "/" + kids + "/" + infants + "/" + isDomestic,
        { state: { searchQuery } }
      );
    }
  };

  // New UI helper functions
  const handleSwap = () => {
    const tempFrom = displayFrom;
    const tempTo = displayTo;
    const tempFromCode = locationFrom;
    const tempToCode = locationTo;
    const tempFromSelected = selectedDeparture;
    const tempToSelected = selectedArrival;
    
    setDisplayFrom(tempTo);
    setDisplayTo(tempFrom);
    setLocationFrom(tempToCode);
    setLocationTo(tempFromCode);
    setSelectedDeparture(tempToSelected);
    setSelectedArrival(tempFromSelected);
    setFrom(tempToSelected?.cityName || "");
    setTo(tempFromSelected?.cityName || "");
  };

  // Multi-city functions (for UI only)
  const addSegment = () => {
    const newId = multiCitySegments.length;
    const lastSegment = multiCitySegments[multiCitySegments.length - 1];
    setMultiCitySegments([
      ...multiCitySegments,
      {
        id: newId,
        from: lastSegment?.to || "",
        to: "",
        date: new Date(new Date().setDate(new Date().getDate() + (newId * 5)))
      }
    ]);
  };

  const removeSegment = (id) => {
    if (multiCitySegments.length > 1) {
      setMultiCitySegments(multiCitySegments.filter(seg => seg.id !== id));
    }
  };

  const updateSegment = (id, field, value) => {
    setMultiCitySegments(multiCitySegments.map(seg => 
      seg.id === id ? { ...seg, [field]: value } : seg
    ));
  };

  // Get passenger display text for new UI
  const getPassengerDisplay = () => {
    return `${adults} Adult${adults !== 1 ? 's' : ''}${
      kids > 0 ? `, ${kids} Kid${kids !== 1 ? 's' : ''}` : ''
    }${
      infants > 0 ? `, ${infants} Infant${infants !== 1 ? 's' : ''}` : ''
    } & ${cabinClass}`;
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target) && 
          fromInputRef.current && !fromInputRef.current.contains(event.target)) {
        setDepartureResults([]);
      }
      
      if (toDropdownRef.current && !toDropdownRef.current.contains(event.target) && 
          toInputRef.current && !toInputRef.current.contains(event.target)) {
        setArrivalResults([]);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div className="sf1-card-body">
      <div className="sf1-search-card-compact">
        {/* Trip Type Selection - New UI */}
        <div className="sf1-trip-type-row">
          <label className="sf1-trip-radio">
            <input
              type="radio"
              name="tripType"
              value="ONEWAY"
              checked={journeyType === "ONEWAY"}
              onChange={(e) => handleJourneyTypeChange(e, "ONEWAY")}
            />
            <RiArrowRightLine className="sf1-trip-icon" /> One way
          </label>
          <label className="sf1-trip-radio">
            <input
              type="radio"
              name="tripType"
              value="RETURN"
              checked={journeyType === "RETURN"}
              onChange={(e) => handleJourneyTypeChange(e, "RETURN")}
            />
            <RiExchangeLine className="sf1-trip-icon" /> Round trip
          </label>
          <label className="sf1-trip-radio">
            <input
              type="radio"
              name="tripType"
              value="MULTI"
              checked={journeyType === "MULTI"}
              onChange={(e) => handleJourneyTypeChange(e, "MULTI")}
            />
            <RiStackLine className="sf1-trip-icon" /> Multi-city
          </label>
        </div>

        {/* Dynamic Flight Search Container */}
        <div id="sf1-dynamicFlightSearch">
          {journeyType !== "MULTI" ? (
            /* Default View - One way / Round trip */
            <div className="sf1-main-row">
              <div className="sf1-flight-fields">
                {/* From Field */}
                <div className="sf1-search-field-sm">
                  <label>FROM</label>
                  <div className="sf1-input-with-icon">
                    <RiFlightTakeoffLine className="sf1-input-icon" />
                    <input
                      ref={fromInputRef}
                      type="text"
                      value={displayFrom}
                      onChange={handleDepartureAirportSearch}
                      onFocus={handleDepartureAirportClick}
                      onClick={handleDepartureAirportClick}
                      placeholder="Search for an airport"
                      autoComplete="off"
                    />
                  </div>
                  {departureResults.length > 0 && (
                    <ul className="sf1-airport-dropdown" ref={fromDropdownRef}>
                      {departureResults.map((airport) => (
                        <li
                          key={airport.airportCode}
                          className="sf1-airport-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDepartureSelect(airport);
                          }}
                        >
                          <div className="sf1-airport-name">
                            {airport.cityName} ({airport.airportCode})
                          </div>
                          <div className="sf1-airport-details">
                            {airport.airportName}, {airport.countryName}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Swap Icon */}
                <div className="sf1-swap-container">
                  <button className="sf1-swap-button" onClick={handleSwap} type="button">
                    <RiSwapLine />
                  </button>
                </div>

                {/* To Field */}
                <div className="sf1-search-field-sm">
                  <label>TO</label>
                  <div className="sf1-input-with-icon">
                    <RiFlightLandLine className="sf1-input-icon" />
                    <input
                      ref={toInputRef}
                      type="text"
                      value={displayTo}
                      onChange={handleArrivalAirportSearch}
                      onFocus={handleArrivalAirportClick}
                      onClick={handleArrivalAirportClick}
                      placeholder="Search for an airport"
                      autoComplete="off"
                    />
                  </div>
                  {arrivalResults.length > 0 && (
                    <ul className="sf1-airport-dropdown" ref={toDropdownRef}>
                      {arrivalResults.map((airport) => (
                        <li
                          key={airport.airportCode}
                          className="sf1-airport-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleArrivalSelect(airport);
                          }}
                        >
                          <div className="sf1-airport-name">
                            {airport.cityName} ({airport.airportCode})
                          </div>
                          <div className="sf1-airport-details">
                            {airport.airportName}, {airport.countryName}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Departure Date */}
                <div className="sf1-search-field-sm">
                  <label>DEPARTURE DATE</label>
                  <DatePicker
                    selected={departureDate}
                    onChange={handleDepartureDateChange}
                    customInput={<CustomDateInput placeholder="DEPARTURE DATE" />}
                    dateFormat="dd-MMM-yyyy"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    minDate={new Date()}
                    yearDropdownItemNumber={50}
                    scrollableYearDropdown
                    placeholderText="DEPARTURE DATE"
                  />
                </div>

                {/* Return Date (for roundtrip) */}
                {journeyType === "RETURN" && (
                  <div className="sf1-search-field-sm">
                    <label>RETURN DATE</label>
                    <DatePicker
                      selected={returnDate}
                      onChange={handleReturnDateChange}
                      customInput={<CustomDateInput placeholder="RETURN DATE" />}
                      dateFormat="dd-MMM-yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      minDate={departureDate}
                      yearDropdownItemNumber={50}
                      scrollableYearDropdown
                      placeholderText="RETURN DATE"
                    />
                  </div>
                )}
              </div>

              {/* Extra Options */}
              <div className="sf1-extra-options">
                <div className="sf1-passenger-wrapper">
                  <label>TRAVELLERS & CLASS</label>
                  <div className="sf1-passenger-dropdown-container">
                    <div className="sf1-passenger-select">
                      {getPassengerDisplay()}
                    </div>
                    
                    <div className="sf1-passenger-controls">
                      <div className="sf1-passenger-row">
                        <span>Adults (12+ yrs)</span>
                        <div className="sf1-input-step">
                          <button type="button" className="sf1-step-btn" onClick={handleDecreaseAdults}>-</button>
                          <span className="sf1-step-value">{adults}</span>
                          <button type="button" className="sf1-step-btn" onClick={handleIncreaseAdults}>+</button>
                        </div>
                      </div>
                      <div className="sf1-passenger-row">
                        <span>Kids (2-11 yrs)</span>
                        <div className="sf1-input-step">
                          <button type="button" className="sf1-step-btn" onClick={handleDecreaseKids}>-</button>
                          <span className="sf1-step-value">{kids}</span>
                          <button type="button" className="sf1-step-btn" onClick={handleIncreaseKids}>+</button>
                        </div>
                      </div>
                      <div className="sf1-passenger-row">
                        <span>Infants (0-2 yrs)</span>
                        <div className="sf1-input-step">
                          <button type="button" className="sf1-step-btn" onClick={handleDecreaseInfants}>-</button>
                          <span className="sf1-step-value">{infants}</span>
                          <button type="button" className="sf1-step-btn" onClick={handleIncreaseInfants}>+</button>
                        </div>
                      </div>
                      <div className="sf1-cabin-options">
                        <div className="sf1-cabin-row">
                          <label className="sf1-cabin-check">
                            <input
                              type="radio"
                              name="cabinClass"
                              value="ECONOMY"
                              checked={cabinClass === "ECONOMY"}
                              onChange={handleClassType}
                            />
                            REGULAR
                          </label>
                          <label className="sf1-cabin-check">
                            <input
                              type="radio"
                              name="cabinClass"
                              value="BUSINESS"
                              checked={cabinClass === "BUSINESS"}
                              onChange={handleClassType}
                            />
                            BUSINESS
                          </label>
                        </div>
                        <div className="sf1-cabin-row">
                          <label className="sf1-cabin-check">
                            <input
                              type="radio"
                              name="cabinClass"
                              value="FIRST"
                              checked={cabinClass === "FIRST"}
                              onChange={handleClassType}
                            />
                            FIRST
                          </label>
                          <label className="sf1-cabin-check">
                            <input
                              type="radio"
                              name="cabinClass"
                              value="PREMIUM_ECONOMY"
                              checked={cabinClass === "PREMIUM_ECONOMY"}
                              onChange={handleClassType}
                            />
                            PREMIUM ECONOMY
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Search Button */}
                <button className="sf1-search-btn-sm" onClick={handleSearch}>
                  <i className="fas fa-search"></i> Search
                </button>
              </div>
            </div>
          ) : (
            /* Multi-city View - For UI only */
            <div className="sf1-multi-city-container">
              {multiCitySegments.map((segment, index) => (
                <div className="sf1-city-segment" key={segment.id}>
                  <div className="sf1-search-field-sm">
                    <label>FROM</label>
                    <div className="sf1-input-with-icon">
                      <RiFlightTakeoffLine className="sf1-input-icon" />
                      <input
                        type="text"
                        value={segment.from}
                        onChange={(e) => updateSegment(segment.id, "from", e.target.value)}
                        placeholder="Search for an airport"
                      />
                    </div>
                  </div>
                  <div className="sf1-search-field-sm">
                    <label>TO</label>
                    <div className="sf1-input-with-icon">
                      <RiFlightLandLine className="sf1-input-icon" />
                      <input
                        type="text"
                        value={segment.to}
                        onChange={(e) => updateSegment(segment.id, "to", e.target.value)}
                        placeholder="Search for an airport"
                      />
                    </div>
                  </div>
                  <div className="sf1-search-field-sm">
                    <label>DEPARTURE DATE</label>
                    <DatePicker
                      selected={segment.date}
                      onChange={(date) => updateSegment(segment.id, "date", date)}
                      customInput={<CustomDateInput placeholder="DEPARTURE DATE" />}
                      dateFormat="dd-MMM-yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      minDate={new Date()}
                      yearDropdownItemNumber={50}
                      scrollableYearDropdown
                      placeholderText="DEPARTURE DATE"
                    />
                  </div>
                  <button 
                    type="button"
                    className="sf1-remove-seg"
                    onClick={() => removeSegment(segment.id)}
                    style={{ visibility: index === 0 ? "hidden" : "visible" }}
                    disabled={index === 0}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
              
              <button type="button" className="sf1-add-seg-btn" onClick={addSegment}>
                <i className="fas fa-plus"></i> Add another flight
              </button>

              {/* Extra Options for Multi-city */}
              <div className="sf1-extra-options" style={{ marginTop: "15px" }}>
                <div className="sf1-passenger-wrapper">
                  <label>TRAVELLERS & CLASS</label>
                  <div className="sf1-passenger-dropdown-container">
                    <div className="sf1-passenger-select">
                      {getPassengerDisplay()}
                    </div>
                    
                    <div className="sf1-passenger-controls">
                      <div className="sf1-passenger-row">
                        <span>Adults (12+ yrs)</span>
                        <div className="sf1-input-step">
                          <button type="button" className="sf1-step-btn" onClick={handleDecreaseAdults}>-</button>
                          <span className="sf1-step-value">{adults}</span>
                          <button type="button" className="sf1-step-btn" onClick={handleIncreaseAdults}>+</button>
                        </div>
                      </div>
                      <div className="sf1-passenger-row">
                        <span>Kids (2-11 yrs)</span>
                        <div className="sf1-input-step">
                          <button type="button" className="sf1-step-btn" onClick={handleDecreaseKids}>-</button>
                          <span className="sf1-step-value">{kids}</span>
                          <button type="button" className="sf1-step-btn" onClick={handleIncreaseKids}>+</button>
                        </div>
                      </div>
                      <div className="sf1-passenger-row">
                        <span>Infants (0-2 yrs)</span>
                        <div className="sf1-input-step">
                          <button type="button" className="sf1-step-btn" onClick={handleDecreaseInfants}>-</button>
                          <span className="sf1-step-value">{infants}</span>
                          <button type="button" className="sf1-step-btn" onClick={handleIncreaseInfants}>+</button>
                        </div>
                      </div>
                      <div className="sf1-cabin-options">
                        <div className="sf1-cabin-row">
                          <label className="sf1-cabin-check">
                            <input
                              type="radio"
                              name="cabinClass"
                              value="ECONOMY"
                              checked={cabinClass === "ECONOMY"}
                              onChange={handleClassType}
                            />
                            REGULAR
                          </label>
                          <label className="sf1-cabin-check">
                            <input
                              type="radio"
                              name="cabinClass"
                              value="BUSINESS"
                              checked={cabinClass === "BUSINESS"}
                              onChange={handleClassType}
                            />
                            BUSINESS
                          </label>
                        </div>
                        <div className="sf1-cabin-row">
                          <label className="sf1-cabin-check">
                            <input
                              type="radio"
                              name="cabinClass"
                              value="FIRST"
                              checked={cabinClass === "FIRST"}
                              onChange={handleClassType}
                            />
                            FIRST
                          </label>
                          <label className="sf1-cabin-check">
                            <input
                              type="radio"
                              name="cabinClass"
                              value="PREMIUM_ECONOMY"
                              checked={cabinClass === "PREMIUM_ECONOMY"}
                              onChange={handleClassType}
                            />
                            PREMIUM ECONOMY
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button type="button" className="sf1-search-btn-sm" onClick={handleSearch}>
                  <i className="fas fa-search"></i> Search
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Preference Options Row */}
        <div className="sf1-preference-row">
          {["REGULAR", "STUDENT", "CORPORATE", "SENIOR_CITIZEN"].map((pref) => (
            <div className="sf1-form-check-inline" key={pref}>
              <input
                type="radio"
                name="preferred"
                id={`sf1-${pref.toLowerCase()}`}
                value={pref}
                checked={preferred === pref}
                onChange={handlePreferredType}
              />
              <label htmlFor={`sf1-${pref.toLowerCase()}`}>
                {pref.replace("_", " ")}
              </label>
            </div>
          ))}
          <div className="sf1-form-check-inline">
            <input
              type="checkbox"
              id="sf1-directFlight"
              checked={isDirect}
              onChange={handleDirectType}
            />
            <label htmlFor="sf1-directFlight">DIRECT FLIGHT?</label>
          </div>
          <div className="sf1-form-check-inline">
            <input
              type="checkbox"
              id="sf1-connectingFlight"
              checked={isConnecting}
              onChange={handleConnectingType}
            />
            <label htmlFor="sf1-connectingFlight">CONNECTING FLIGHT?</label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchFlightPanel1;